import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { usePackagesStore } from "../stores/packagesStore";
import { useAuthStore } from "../stores/authStore";
import type { Package, PackageItem, PackageCollaborator, Operator, PackageAuditLog } from "../types/database";

// ============================================
// PACKAGES
// ============================================

export function usePackages() {
  const { user } = useAuthStore();
  const { setPackages } = usePackagesStore();

  return useQuery({
    queryKey: ["packages", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // Query packages owned by user
      const { data: ownedPackages, error: ownedError } = await supabase
        .from("packages")
        .select(`
          *,
          items:package_items(*),
          collaborators:package_collaborators(*)
        `)
        .eq("owner_id", user.id)
        .order("created_at", { ascending: false });

      if (ownedError) throw ownedError;
      
      // Query packages where user is collaborator
      const { data: collabData } = await supabase
        .from("package_collaborators")
        .select("package_id")
        .eq("user_id", user.id);
      
      let collaboratedPackages: Package[] = [];
      if (collabData && collabData.length > 0) {
        const packageIds = collabData.map(c => c.package_id);
        const { data: collabPackages } = await supabase
          .from("packages")
          .select(`
            *,
            items:package_items(*),
            collaborators:package_collaborators(*)
          `)
          .in("id", packageIds)
          .order("created_at", { ascending: false });
        
        collaboratedPackages = (collabPackages || []) as Package[];
      }
      
      // Merge and deduplicate
      const allPackages = [...(ownedPackages || []), ...collaboratedPackages];
      const uniquePackages = allPackages.filter((pkg, index, self) =>
        index === self.findIndex(p => p.id === pkg.id)
      ) as Package[];
      
      setPackages(uniquePackages);
      return uniquePackages;
    },
    enabled: !!user?.id,
  });
}

export function usePackage(id: string | null) {
  const { setSelectedPackage } = usePackagesStore();

  return useQuery({
    queryKey: ["package", id],
    queryFn: async () => {
      if (!id) return null;
      
      const { data, error } = await supabase
        .from("packages")
        .select(`
          *,
          items:package_items(
            *,
            operator:operators(id, name, typology, city, region)
          ),
          collaborators:package_collaborators(
            *,
            user:users(id, first_name, last_name, email)
          )
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      
      const pkg = data as Package;
      setSelectedPackage(pkg);
      return pkg;
    },
    enabled: !!id,
  });
}

export function useCreatePackage() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { addPackage } = usePackagesStore();

  return useMutation({
    mutationFn: async (packageData: {
      name: string;
      description?: string;
      start_date?: string;
      end_date?: string;
      cover_image_url?: string;
    }) => {
      if (!user?.id) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("packages")
        .insert({
          ...packageData,
          owner_id: user.id,
          status: "draft",
        })
        .select()
        .single();

      if (error) throw error;
      return data as Package;
    },
    onSuccess: (data) => {
      addPackage(data);
      queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
  });
}

export function useUpdatePackage() {
  const queryClient = useQueryClient();
  const { updatePackage } = usePackagesStore();

  return useMutation({
    mutationFn: async ({ 
      id, 
      ...updates 
    }: Partial<Package> & { id: string }) => {
      const { data, error } = await supabase
        .from("packages")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Package;
    },
    onSuccess: (data) => {
      updatePackage(data.id, data);
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      queryClient.invalidateQueries({ queryKey: ["package", data.id] });
      // Refresh audit log to show the update
      queryClient.invalidateQueries({ queryKey: ["package-audit-log", data.id] });
    },
  });
}

export function useDeletePackage() {
  const queryClient = useQueryClient();
  const { removePackage } = usePackagesStore();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("packages")
        .delete()
        .eq("id", id);

      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      removePackage(id);
      queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
  });
}

// ============================================
// PACKAGE ITEMS
// ============================================

export function useAddPackageItem() {
  const queryClient = useQueryClient();
  const { addPackageItem } = usePackagesStore();

  return useMutation({
    mutationFn: async (item: Omit<PackageItem, "id" | "created_at">) => {
      const { data, error } = await supabase
        .from("package_items")
        .insert(item)
        .select(`
          *,
          operator:operators(id, name, typology, city, region)
        `)
        .single();

      if (error) throw error;
      return data as PackageItem;
    },
    onSuccess: (data) => {
      addPackageItem(data);
      queryClient.invalidateQueries({ queryKey: ["package", data.package_id] });
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      // Refresh audit log to show the new item
      queryClient.invalidateQueries({ queryKey: ["package-audit-log", data.package_id] });
    },
  });
}

export function useUpdatePackageItem() {
  const queryClient = useQueryClient();
  const { updatePackageItem } = usePackagesStore();

  return useMutation({
    mutationFn: async ({ 
      id, 
      packageId,
      ...updates 
    }: Partial<PackageItem> & { id: string; packageId: string }) => {
      const { data, error } = await supabase
        .from("package_items")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return { item: data as PackageItem, packageId };
    },
    onSuccess: ({ item, packageId }) => {
      updatePackageItem(item.id, item);
      queryClient.invalidateQueries({ queryKey: ["package", packageId] });
    },
  });
}

export function useDeletePackageItem() {
  const queryClient = useQueryClient();
  const { removePackageItem } = usePackagesStore();

  return useMutation({
    mutationFn: async ({ id, packageId }: { id: string; packageId: string }) => {
      const { error } = await supabase
        .from("package_items")
        .delete()
        .eq("id", id);

      if (error) throw error;
      return { id, packageId };
    },
    onSuccess: ({ id, packageId }) => {
      removePackageItem(id);
      queryClient.invalidateQueries({ queryKey: ["package", packageId] });
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      // Refresh audit log to show item removal
      queryClient.invalidateQueries({ queryKey: ["package-audit-log", packageId] });
    },
  });
}

export function useReorderPackageItems() {
  const queryClient = useQueryClient();
  const { reorderPackageItems } = usePackagesStore();

  return useMutation({
    mutationFn: async ({ 
      packageId, 
      items 
    }: { 
      packageId: string; 
      items: PackageItem[];
    }) => {
      // Update sort_order for each item
      const updates = items.map((item, index) => 
        supabase
          .from("package_items")
          .update({ sort_order: index })
          .eq("id", item.id)
      );

      await Promise.all(updates);
      return { packageId, items };
    },
    onSuccess: ({ packageId, items }) => {
      reorderPackageItems(items);
      queryClient.invalidateQueries({ queryKey: ["package", packageId] });
    },
  });
}

// ============================================
// PACKAGE COLLABORATORS
// ============================================

export function useInviteCollaborator() {
  const queryClient = useQueryClient();
  const { addCollaborator } = usePackagesStore();

  return useMutation({
    mutationFn: async ({ 
      packageId, 
      userId, 
      role = "collaborator" 
    }: { 
      packageId: string; 
      userId: string; 
      role?: PackageCollaborator["role"];
    }) => {
      const { data, error } = await supabase
        .from("package_collaborators")
        .insert({
          package_id: packageId,
          user_id: userId,
          role,
          status: "pending",
        })
        .select(`
          *,
          user:users(id, first_name, last_name, email)
        `)
        .single();

      if (error) throw error;
      return data as PackageCollaborator;
    },
    onSuccess: (data) => {
      addCollaborator(data);
      queryClient.invalidateQueries({ queryKey: ["package", data.package_id] });
      // Refresh audit log to show collaborator invitation
      queryClient.invalidateQueries({ queryKey: ["package-audit-log", data.package_id] });
    },
  });
}

export function useRespondToInvite() {
  const queryClient = useQueryClient();
  const { updateCollaborator } = usePackagesStore();

  return useMutation({
    mutationFn: async ({ 
      id, 
      status,
      packageId,
    }: { 
      id: string; 
      status: "accepted" | "rejected";
      packageId: string;
    }) => {
      const { data, error } = await supabase
        .from("package_collaborators")
        .update({
          status,
          responded_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return { collaborator: data as PackageCollaborator, packageId };
    },
    onSuccess: ({ collaborator, packageId }) => {
      updateCollaborator(collaborator.id, collaborator);
      queryClient.invalidateQueries({ queryKey: ["package", packageId] });
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      // Refresh audit log to show collaborator response
      queryClient.invalidateQueries({ queryKey: ["package-audit-log", packageId] });
    },
  });
}

export function useRemoveCollaborator() {
  const queryClient = useQueryClient();
  const { removeCollaborator } = usePackagesStore();

  return useMutation({
    mutationFn: async ({ id, packageId }: { id: string; packageId: string }) => {
      const { error } = await supabase
        .from("package_collaborators")
        .delete()
        .eq("id", id);

      if (error) throw error;
      return { id, packageId };
    },
    onSuccess: ({ id, packageId }) => {
      removeCollaborator(id);
      queryClient.invalidateQueries({ queryKey: ["package", packageId] });
    },
  });
}

// ============================================
// PUBLISH PACKAGE
// ============================================

export function usePublishPackage() {
  const queryClient = useQueryClient();
  const { updatePackage } = usePackagesStore();

  return useMutation({
    mutationFn: async (id: string) => {
      // Check if there are pending collaborators
      const { data: collaborators } = await supabase
        .from("package_collaborators")
        .select("status")
        .eq("package_id", id)
        .neq("role", "owner");

      const hasPendingCollaborators = collaborators?.some(c => c.status === "pending");
      const newStatus = hasPendingCollaborators ? "pending_approval" : "active";

      const { data, error } = await supabase
        .from("packages")
        .update({
          status: newStatus,
          published_at: newStatus === "active" ? new Date().toISOString() : null,
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Package;
    },
    onSuccess: (data) => {
      updatePackage(data.id, data);
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      queryClient.invalidateQueries({ queryKey: ["package", data.id] });
      // Refresh audit log to show publish action
      queryClient.invalidateQueries({ queryKey: ["package-audit-log", data.id] });
    },
  });
}

// ============================================
// UNPUBLISH PACKAGE
// ============================================

export function useUnpublishPackage() {
  const queryClient = useQueryClient();
  const { updatePackage } = usePackagesStore();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from("packages")
        .update({
          status: "draft",
          published_at: null,
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Package;
    },
    onSuccess: (data) => {
      updatePackage(data.id, data);
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      queryClient.invalidateQueries({ queryKey: ["package", data.id] });
      // Refresh audit log to show unpublish action
      queryClient.invalidateQueries({ queryKey: ["package-audit-log", data.id] });
    },
  });
}

// ============================================
// OPERATOR SEARCH FOR PACKAGES
// ============================================

export function useSearchOperators(searchQuery: string, limit: number = 20) {
  return useQuery({
    queryKey: ["operators-search", searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];

      const { data, error } = await supabase
        .from("operators")
        .select("id, name, typology, city, region, macro_category, email")
        .or(`name.ilike.%${searchQuery}%,city.ilike.%${searchQuery}%`)
        .limit(limit);

      if (error) throw error;
      return data as Operator[];
    },
    enabled: searchQuery.length >= 2,
    staleTime: 30000, // 30 seconds
  });
}

// ============================================
// PACKAGE AUDIT LOG
// ============================================

export function usePackageAuditLog(packageId: string | null, limit: number = 50) {
  return useQuery({
    queryKey: ["package-audit-log", packageId],
    queryFn: async () => {
      if (!packageId) return [];

      const { data, error } = await supabase
        .from("package_audit_log")
        .select(`
          *,
          user:users(id, first_name, last_name, email)
        `)
        .eq("package_id", packageId)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as PackageAuditLog[];
    },
    enabled: !!packageId,
  });
}

